/** @module @liquidefy/code-editor/document/document-cursor @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  Position,
  Range,
  Selection,
  EditorResult,
  CursorState,
  CursorGroup,
  CursorGroupId,
  ColumnSelection,
} from '../code-editor-types.js';

import {
  ok,
  err,
  comparePositions,
  positionToOffset,
  offsetToPosition,
  asCursorGroupId,
  generateId,
  clamp,
} from '../code-editor.js';

// ═══ §2 CURSOR MOVEMENT — directional movement, word/subword/bracket navigation ═══

export function moveUp(cursor: CursorState, content: string, lines: number = 1): CursorState {
  const allLines = splitLines(content);
  const newLine = Math.max(0, cursor.position.line - lines);
  const lineLen = allLines![newLine]!.length;
  const col = Math.min(cursor.desiredColumn, lineLen);

  return {
    position: { line: newLine, column: col },
    desiredColumn: cursor.desiredColumn,
  };
}

export function moveDown(cursor: CursorState, content: string, lines: number = 1): CursorState {
  const allLines = splitLines(content);
  const newLine = Math.min(allLines.length - 1, cursor.position.line + lines);
  const lineLen = allLines![newLine]!.length;
  const col = Math.min(cursor.desiredColumn, lineLen);

  return {
    position: { line: newLine, column: col },
    desiredColumn: cursor.desiredColumn,
  };
}

export function moveLeft(cursor: CursorState, content: string): CursorState {
  const allLines = splitLines(content);
  let { line, column } = cursor.position;

  if (column > 0) {
    column--;
  } else if (line > 0) {
    line--;
    column = allLines![line]!.length;
  }

  return {
    position: { line, column },
    desiredColumn: column,
  };
}

export function moveRight(cursor: CursorState, content: string): CursorState {
  const allLines = splitLines(content);
  let { line, column } = cursor.position;

  if (column < allLines[line]!.length) {
    column++;
  } else if (line < allLines.length - 1) {
    line++;
    column = 0;
  }

  return {
    position: { line, column },
    desiredColumn: column,
  };
}

export function moveToWordBoundary(
  cursor: CursorState,
  content: string,
  direction: 'left' | 'right',
  wordSeparators: string = ' \t\n\r.,;:!?()[]{}\'"/\\-+=<>*&^%$#@~`|',
): CursorState {
  const allLines = splitLines(content);
  let { line, column } = cursor.position;

  if (direction === 'right') {
    const currentLine = allLines[line]!;
    if (column >= currentLine!.length) {
      if (line < allLines.length - 1) {
        return { position: { line: line + 1, column: 0 }, desiredColumn: 0 };
      }
      return cursor;
    }

    let i = column;
    const isWordChar = (ch: string) => !wordSeparators.includes(ch);
    const startIsWord = isWordChar(currentLine[i]!);

    while (i < currentLine!.length) {
      const charIsWord = isWordChar(currentLine[i]!);
      if (charIsWord !== startIsWord) break;
      i++;
    }

    while (i < currentLine!.length && currentLine[i]! === ' ') i++;

    return { position: { line, column: i }, desiredColumn: i };
  }

  if (column <= 0) {
    if (line > 0) {
      const prevLen = allLines[line - 1]!.length;
      return { position: { line: line - 1, column: prevLen }, desiredColumn: prevLen };
    }
    return cursor;
  }

  const currentLine = allLines[line]!;
  let i = column - 1;

  while (i > 0 && currentLine[i - 1]! === ' ') i--;

  if (i > 0) {
    const isWordChar = (ch: string) => !wordSeparators.includes(ch);
    const startIsWord = isWordChar(currentLine[i - 1]!);

    while (i > 0 && isWordChar(currentLine[i - 1]!) === startIsWord) i--;
  }

  return { position: { line, column: i }, desiredColumn: i };
}

export function moveToSubwordBoundary(
  cursor: CursorState,
  content: string,
  direction: 'left' | 'right',
): CursorState {
  const allLines = splitLines(content);
  let { line, column } = cursor.position;
  const currentLine = allLines[line]!;

  if (direction === 'right') {
    if (column >= currentLine!.length) {
      if (line < allLines.length - 1) return { position: { line: line + 1, column: 0 }, desiredColumn: 0 };
      return cursor;
    }

    let i = column;
    const ch = currentLine[i]!;

    if (isUpperCase(ch!)) {
      i++;
      if (i < currentLine!.length && isUpperCase(currentLine[i]!)) {
        while (i < currentLine!.length && isUpperCase(currentLine[i]!)) {
          if (i + 1 < currentLine!.length && isLowerCase(currentLine[i + 1]!)) break;
          i++;
        }
      } else {
        while (i < currentLine!.length && isLowerCase(currentLine[i]!)) i++;
      }
    } else if (isLowerCase(ch!)) {
      while (i < currentLine!.length && isLowerCase(currentLine[i]!)) i++;
    } else if (ch === '_' || ch === '-') {
      while (i < currentLine!.length && (currentLine[i]! === '_' || currentLine[i]! === '-')) i++;
    } else {
      i++;
    }

    return { position: { line, column: i }, desiredColumn: i };
  }

  if (column <= 0) {
    if (line > 0) {
      const prevLen = allLines[line - 1]!.length;
      return { position: { line: line - 1, column: prevLen }, desiredColumn: prevLen };
    }
    return cursor;
  }

  let i = column;

  if (i > 0 && (currentLine[i - 1]! === '_' || currentLine[i - 1]! === '-')) {
    while (i > 0 && (currentLine[i - 1]! === '_' || currentLine[i - 1]! === '-')) i--;
    return { position: { line, column: i }, desiredColumn: i };
  }

  if (i > 0 && isLowerCase(currentLine[i - 1]!)) {
    while (i > 0 && isLowerCase(currentLine[i - 1]!)) i--;
    return { position: { line, column: i }, desiredColumn: i };
  }

  if (i > 0 && isUpperCase(currentLine[i - 1]!)) {
    while (i > 0 && isUpperCase(currentLine[i - 1]!)) i--;
    if (i > 0 && isLowerCase(currentLine[i]!)) {
      // Keep at boundary
    }
    return { position: { line, column: i }, desiredColumn: i };
  }

  i--;
  return { position: { line, column: i }, desiredColumn: i };
}

export function moveToBracketMatch(
  cursor: CursorState,
  content: string,
  brackets: Array<{ open: string; close: string }>,
): CursorState {
  const allLines = splitLines(content);
  const { line, column } = cursor.position;
  const currentLine = allLines[line]!;

  if (column >= currentLine!.length) return cursor;

  const ch = currentLine[column]!;

  for (const pair of brackets) {
    if (ch === pair.open) {
      const matchPos = findMatchingBracket(content, cursor.position, pair.open, pair.close, 'forward');
      if (matchPos) return { position: matchPos, desiredColumn: matchPos.column };
    }
    if (ch === pair.close) {
      const matchPos = findMatchingBracket(content, cursor.position, pair.close, pair.open, 'backward');
      if (matchPos) return { position: matchPos, desiredColumn: matchPos.column };
    }
  }

  return cursor;
}

export function moveToDocumentStart(): CursorState {
  return { position: { line: 0, column: 0 }, desiredColumn: 0 };
}

export function moveToDocumentEnd(content: string): CursorState {
  const allLines = splitLines(content);
  const lastLine = allLines.length - 1;
  const lastCol = allLines[lastLine]!.length;
  return { position: { line: lastLine, column: lastCol }, desiredColumn: lastCol };
}

export function moveToLineStart(
  cursor: CursorState,
  content: string,
  smartHome: boolean = true,
): CursorState {
  const allLines = splitLines(content);
  const { line } = cursor.position;
  const currentLine = allLines[line]!;

  if (smartHome) {
    const firstNonWs = currentLine!.search(/\S/);
    const targetCol = firstNonWs < 0 ? 0 : firstNonWs;

    if (cursor.position.column !== targetCol) {
      return { position: { line, column: targetCol }, desiredColumn: targetCol };
    }
  }

  return { position: { line, column: 0 }, desiredColumn: 0 };
}

export function moveToLineEnd(cursor: CursorState, content: string): CursorState {
  const allLines = splitLines(content);
  const { line } = cursor.position;
  const col = allLines[line]!.length;
  return { position: { line, column: col }, desiredColumn: col };
}

// ═══ §3 SELECTION OPERATIONS — select word, line, bracket content, etc. ═══

export function selectToPosition(cursor: CursorState, position: Position): Selection {
  const anchor = cursor.selectionAnchor ?? cursor.position;
  const isReversed = comparePositions(position, anchor) < 0;
  return { anchor, active: position, isReversed };
}

export function selectWord(cursor: CursorState, content: string): Selection {
  const allLines = splitLines(content);
  const { line, column } = cursor.position;
  const currentLine = allLines[line]!;

  if (currentLine!.length === 0) {
    const pos = { line, column: 0 };
    return { anchor: pos, active: pos, isReversed: false };
  }

  const col = Math.min(column, currentLine!.length - 1);
  const isWordCh = (ch: string) => /\w/.test(ch);

  if (!isWordCh(currentLine[col]!)) {
    const pos = cursor.position;
    return { anchor: pos, active: { line, column: col + 1 }, isReversed: false };
  }

  let start = col;
  while (start > 0 && isWordCh(currentLine[start - 1]!)) start--;
  let end = col;
  while (end < currentLine!.length - 1 && isWordCh(currentLine[end + 1]!)) end++;

  return {
    anchor: { line, column: start },
    active: { line, column: end + 1 },
    isReversed: false,
  };
}

export function selectLine(cursor: CursorState, content: string): Selection {
  const allLines = splitLines(content);
  const { line } = cursor.position;
  const lineLen = allLines[line]!.length;

  return {
    anchor: { line, column: 0 },
    active: { line, column: lineLen },
    isReversed: false,
  };
}

export function selectBracketContent(
  cursor: CursorState,
  content: string,
  brackets: Array<{ open: string; close: string }>,
): Selection | null {
  const { line, column } = cursor.position;

  for (const pair of brackets) {
    const openPos = findMatchingBracket(content, cursor.position, pair.close, pair.open, 'backward');
    if (openPos) {
      const closePos = findMatchingBracket(content, openPos, pair.open, pair.close, 'forward');
      if (closePos) {
        const innerStart: Position = { line: openPos.line, column: openPos.column + 1 };
        return { anchor: innerStart, active: closePos, isReversed: false };
      }
    }
  }

  return null;
}

export function selectIndentation(cursor: CursorState, content: string): Selection {
  const allLines = splitLines(content);
  const { line } = cursor.position;
  const currentLine = allLines[line]!;
  const firstNonWs = currentLine!.search(/\S/);
  const end = firstNonWs < 0 ? currentLine!.length : firstNonWs;

  return {
    anchor: { line, column: 0 },
    active: { line, column: end },
    isReversed: false,
  };
}

export function selectAll(content: string): Selection {
  const allLines = splitLines(content);
  const lastLine = allLines.length - 1;
  return {
    anchor: { line: 0, column: 0 },
    active: { line: lastLine, column: allLines[lastLine]!.length },
    isReversed: false,
  };
}

export function reverseSelection(selection: Selection): Selection {
  return {
    anchor: selection.active,
    active: selection.anchor,
    isReversed: !selection.isReversed,
  };
}

export function normalizeSelection(selection: Selection): Selection {
  if (comparePositions(selection.anchor, selection.active) <= 0) return selection;
  return {
    anchor: selection.active,
    active: selection.anchor,
    isReversed: true,
  };
}

export function selectionToRange(selection: Selection): Range {
  const cmp = comparePositions(selection.anchor, selection.active);
  if (cmp <= 0) return { start: selection.anchor, end: selection.active };
  return { start: selection.active, end: selection.anchor };
}

export function rangeToSelection(range: Range): Selection {
  return { anchor: range.start, active: range.end, isReversed: false };
}

// ═══ §4 MULTI-CURSOR — add, remove, dedup, iterate ═══

export function addCursor(group: CursorGroup, position: Position): CursorGroup {
  const newCursor: CursorState = { position, desiredColumn: position.column };
  return {
    ...group,
    cursors: [...group.cursors, newCursor],
  };
}

export function removeCursor(group: CursorGroup, index: number): CursorGroup {
  if (group.cursors.length <= 1) return group;
  if (index < 0 || index >= group.cursors.length) return group;

  const newCursors = group.cursors.filter((_, i) => i !== index);
  const newPrimary = group.primaryIndex >= newCursors.length
    ? newCursors.length - 1
    : group.primaryIndex > index
      ? group.primaryIndex - 1
      : group.primaryIndex;

  return { ...group, cursors: newCursors, primaryIndex: newPrimary };
}

export function addCursorAbove(group: CursorGroup, content: string): CursorGroup {
  const primary = group.cursors[group.primaryIndex];
  if (primary!.position.line <= 0) return group;

  const allLines = splitLines(content);
  const newLine = primary!.position.line - 1;
  const newCol = Math.min(primary!.desiredColumn, allLines[newLine]!.length);

  const newCursor: CursorState = {
    position: { line: newLine, column: newCol },
    desiredColumn: primary!.desiredColumn,
  };

  return {
    ...group,
    cursors: [newCursor, ...group.cursors],
    primaryIndex: group.primaryIndex + 1,
  };
}

export function addCursorBelow(group: CursorGroup, content: string): CursorGroup {
  const primary = group.cursors[group.primaryIndex];
  const allLines = splitLines(content);

  if (primary!.position.line >= allLines.length - 1) return group;

  const newLine = primary!.position.line + 1;
  const newCol = Math.min(primary!.desiredColumn, allLines[newLine]!.length);

  const newCursor: CursorState = {
    position: { line: newLine, column: newCol },
    desiredColumn: primary!.desiredColumn,
  };

  return {
    ...group,
    cursors: [...group.cursors, newCursor],
  };
}

export function addCursorsToLineEnds(
  group: CursorGroup,
  content: string,
  startLine: number,
  endLine: number,
): CursorGroup {
  const allLines = splitLines(content);
  const clampedStart = Math.max(0, startLine);
  const clampedEnd = Math.min(allLines.length - 1, endLine);
  const newCursors: CursorState[] = [];

  for (let i = clampedStart; i <= clampedEnd; i++) {
    const col = allLines[i]!.length;
    newCursors.push({ position: { line: i, column: col }, desiredColumn: col });
  }

  return {
    ...group,
    cursors: [...group.cursors, ...newCursors],
  };
}

export function addCursorsAtSearchMatches(
  group: CursorGroup,
  content: string,
  pattern: string,
): CursorGroup {
  const regex = new RegExp(escapeRegex(pattern), 'g');
  const newCursors: CursorState[] = [];
  let match: RegExpExecArray | null;

  while ((match = regex.exec(content)) !== null) {
    const pos = offsetToPosition(content, match.index);
    newCursors.push({ position: pos, desiredColumn: pos.column });
    if (match.index === regex.lastIndex) regex.lastIndex++;
  }

  if (newCursors.length === 0) return group;

  return {
    ...group,
    cursors: [...group.cursors, ...newCursors],
  };
}

export function collapseCursors(group: CursorGroup): CursorGroup {
  const primary = group.cursors[group.primaryIndex]!;
  return {
    ...group,
    cursors: [{ ...primary, selectionAnchor: undefined }],
    primaryIndex: 0,
  };
}

export function dedupCursors(group: CursorGroup): CursorGroup {
  const seen = new Set<string>();
  const deduped: CursorState[] = [];
  let newPrimary = 0;

  for (let i = 0; i < group.cursors.length; i++) {
    const c = group.cursors[i];
    const key = `${c!.position.line}:${c!.position.column}`;
    if (!seen.has(key)) {
      seen.add(key);
      if (i === group.primaryIndex) newPrimary = deduped.length;
      deduped.push(c!);
    }
  }

  return { ...group, cursors: deduped, primaryIndex: newPrimary };
}

export function setPrimaryCursor(group: CursorGroup, index: number): CursorGroup {
  if (index < 0 || index >= group.cursors.length) return group;
  return { ...group, primaryIndex: index };
}

export function forEachCursor(
  group: CursorGroup,
  fn: (cursor: CursorState, index: number) => CursorState,
): CursorGroup {
  return {
    ...group,
    cursors: group.cursors.map((c, i) => fn(c, i)),
  };
}

// ═══ §5 COLUMN SELECTION — block/rectangular selection ═══

export function createColumnSelection(start: Position, end: Position): ColumnSelection {
  const startLine = Math.min(start.line, end.line);
  const endLine = Math.max(start.line, end.line);
  const startColumn = Math.min(start.column, end.column);
  const endColumn = Math.max(start.column, end.column);

  return { startLine, endLine, startColumn, endColumn };
}

export function expandColumnSelection(
  selection: ColumnSelection,
  direction: 'up' | 'down' | 'left' | 'right',
  content: string,
): ColumnSelection {
  const allLines = splitLines(content);

  switch (direction) {
    case 'up':
      return { ...selection, startLine: Math.max(0, selection.startLine - 1) };
    case 'down':
      return { ...selection, endLine: Math.min(allLines.length - 1, selection.endLine + 1) };
    case 'left':
      return { ...selection, startColumn: Math.max(0, selection.startColumn - 1) };
    case 'right':
      return { ...selection, endColumn: selection.endColumn + 1 };
  }
}

export function columnSelectionToRanges(selection: ColumnSelection): Range[] {
  const ranges: Range[] = [];
  for (let line = selection.startLine; line <= selection.endLine; line++) {
    ranges.push({
      start: { line, column: selection.startColumn },
      end: { line, column: selection.endColumn },
    });
  }
  return ranges;
}

export function rangesToColumnSelection(ranges: Range[]): ColumnSelection | null {
  if (ranges.length === 0) return null;

  let startLine = Infinity;
  let endLine = -Infinity;
  let startColumn = Infinity;
  let endColumn = -Infinity;

  for (const r of ranges) {
    startLine = Math.min(startLine, r.start.line);
    endLine = Math.max(endLine, r.end.line);
    startColumn = Math.min(startColumn, r.start.column);
    endColumn = Math.max(endColumn, r.end.column);
  }

  return { startLine, endLine, startColumn, endColumn };
}

export function insertIntoColumnSelection(
  content: string,
  selection: ColumnSelection,
  text: string,
): string {
  const allLines = splitLines(content);
  const eol = detectEol(content);

  for (let line = selection.startLine; line <= selection.endLine && line < allLines.length; line++) {
    const currentLine = allLines[line]!;
    const padded = currentLine!.padEnd(selection.startColumn);
    allLines[line] = padded.slice(0, selection.startColumn) + text + padded.slice(selection.startColumn);
  }

  return allLines.join(eol);
}

// ═══ §6 CURSOR ANALYSIS — context, boundary checks, distance ═══

export function cursorContext(
  cursor: CursorState,
  content: string,
): {
  word: string;
  line: string;
  indentLevel: number;
  isInString: boolean;
  isInComment: boolean;
  bracketDepth: number;
} {
  const allLines = splitLines(content);
  const { line, column } = cursor.position;
  const currentLine = line < allLines.length ? allLines[line]! : '';

  const word = extractWordAt(currentLine!, column);
  const indentLevel = countLeadingWhitespace(currentLine!);

  const textBefore = currentLine!.slice(0, column);
  const isInString = isInsideString(textBefore);
  const isInComment = isInsideComment(textBefore);
  const bracketDepth = computeBracketDepth(content, cursor.position);

  return { word, line: currentLine, indentLevel, isInString, isInComment, bracketDepth };
}

export function isCursorAtWordBoundary(cursor: CursorState, content: string): boolean {
  const allLines = splitLines(content);
  const { line, column } = cursor.position;
  const currentLine = allLines[line]!;

  if (column === 0 || column >= currentLine!.length) return true;

  const before = /\w/.test(currentLine[column - 1]!);
  const after = /\w/.test(currentLine[column]!);
  return before !== after;
}

export function isCursorAtLineStart(cursor: CursorState): boolean {
  return cursor.position.column === 0;
}

export function isCursorAtLineEnd(cursor: CursorState, content: string): boolean {
  const allLines = splitLines(content);
  const { line, column } = cursor.position;
  if (line >= allLines.length) return true;
  return column >= allLines[line]!.length;
}

export function isCursorAtDocumentStart(cursor: CursorState): boolean {
  return cursor.position.line === 0 && cursor.position.column === 0;
}

export function isCursorAtDocumentEnd(cursor: CursorState, content: string): boolean {
  const allLines = splitLines(content);
  const lastLine = allLines.length - 1;
  return cursor.position.line === lastLine && cursor.position.column >= allLines[lastLine]!.length;
}

export function distanceBetweenCursors(a: CursorState, b: CursorState, content: string): number {
  const offsetA = positionToOffset(content, a.position);
  const offsetB = positionToOffset(content, b.position);
  return Math.abs(offsetB - offsetA);
}

// ═══ INTERNAL HELPERS ═══

function splitLines(content: string): string[] {
  return content.split(/\r\n|\r|\n/);
}

function detectEol(content: string): string {
  if (content.includes('\r\n')) return '\r\n';
  if (content.includes('\r')) return '\r';
  return '\n';
}

function isUpperCase(ch: string): boolean {
  return ch >= 'A' && ch <= 'Z';
}

function isLowerCase(ch: string): boolean {
  return ch >= 'a' && ch <= 'z';
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function findMatchingBracket(
  content: string,
  start: Position,
  searchChar: string,
  matchChar: string,
  direction: 'forward' | 'backward',
): Position | null {
  const allLines = splitLines(content);
  let depth = 0;
  let { line, column } = start;

  if (direction === 'forward') {
    column++;
    for (; line < allLines.length; line++) {
      const currentLine = allLines[line]!;
      for (; column < currentLine!.length; column++) {
        const ch = currentLine[column]!;
        if (ch === searchChar) depth++;
        if (ch === matchChar) {
          if (depth === 0) return { line, column };
          depth--;
        }
      }
      column = 0;
    }
  } else {
    column--;
    for (; line >= 0; line--) {
      const currentLine = allLines[line]!;
      if (column < 0) column = currentLine!.length - 1;
      for (; column >= 0; column--) {
        const ch = currentLine[column]!;
        if (ch === searchChar) depth++;
        if (ch === matchChar) {
          if (depth === 0) return { line, column };
          depth--;
        }
      }
      column = -1;
    }
  }

  return null;
}

function extractWordAt(line: string, column: number): string {
  if (line.length === 0) return '';
  const col = clamp(column, 0, line.length - 1);
  if (!/\w/.test(line[col]!)) return '';

  let start = col;
  while (start > 0 && /\w/.test(line[start - 1]!)) start--;
  let end = col;
  while (end < line.length - 1 && /\w/.test(line[end + 1]!)) end++;

  return line.slice(start, end + 1);
}

function countLeadingWhitespace(line: string): number {
  let count = 0;
  for (const ch of line) {
    if (ch === ' ') count++;
    else if (ch === '\t') count += 4;
    else break;
  }
  return count;
}

function isInsideString(textBefore: string): boolean {
  let inSingle = false;
  let inDouble = false;
  let inTemplate = false;

  for (let i = 0; i < textBefore.length; i++) {
    const ch = textBefore[i];
    const prev = i > 0 ? textBefore[i - 1] : '';

    if (prev === '\\') continue;

    if (ch === "'" && !inDouble && !inTemplate) inSingle = !inSingle;
    if (ch === '"' && !inSingle && !inTemplate) inDouble = !inDouble;
    if (ch === '`' && !inSingle && !inDouble) inTemplate = !inTemplate;
  }

  return inSingle || inDouble || inTemplate;
}

function isInsideComment(textBefore: string): boolean {
  const lineCommentIdx = textBefore.lastIndexOf('//');
  if (lineCommentIdx >= 0) {
    const beforeSlash = textBefore.slice(0, lineCommentIdx);
    if (!isInsideString(beforeSlash)) return true;
  }
  return false;
}

function computeBracketDepth(content: string, position: Position): number {
  const offset = positionToOffset(content, position);
  const textBefore = content.slice(0, offset);
  let depth = 0;

  for (const ch of textBefore) {
    if (ch === '(' || ch === '[' || ch === '{') depth++;
    if (ch === ')' || ch === ']' || ch === '}') depth--;
  }

  return Math.max(0, depth);
}
